import { Button } from "@omnitok/ui";
import { Sparkles, X } from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { slugFromPath } from "@/lib/tenant-path";

import { GlobalProgressBar } from "@pervasivemind/bi-components";
import {
  ControlPanel,
  ReportHeader,
  DashboardBody,
  MAIN_TABLE_ID,
  SECTION_STACK_CLASS,
  useFetchActivity,
  type ChartData,
  type TextValue,
  type DashboardDataSource,
  type DrillData as DrillPayload,
  type FetchDrillArgs,
  type FetchRowDrillArgs,
  type FetchTableDataArgs,
  type Kpi,
  type RowDrillData,
  type TableData,
  resolveLocalized,
  type DisplayLabel,
} from "@pervasivemind/dashboard-render";
import {
  applyFilterValues,
  CollapsibleFilterHeader,
  crossFilterWrite,
  GenericFilterBar,
  isDateWindowType,
  reconcileMultiSelectOptions,
  resetAllFilters,
  resetFilterValue,
  resolveSeedValue,
  setFilterValue,
  type DashboardFilter,
} from "@pervasivemind/dashboard-render";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import { useT } from "@/i18n";
import { previewApi, type PreviewRequest } from "@/lib/builder-api";
import { withGroupFilters } from "@/lib/report-group-preview";
import {
  flattenForEngine,
  flattenForRender,
} from "@/pages/builder/preview/flatten";
import type {
  DashboardConfigV2,
  ReportConfig,
  SectionConfig,
} from "@/types";

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  draft: UseBuilderDraft;
}

/**
 * Pixel-perfect dashboard preview.
 *
 * Replicates ``hp-portal/frontend/src/pages/GenericDashboardPage.tsx``:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ Banner gradient — title + date + filterbar    │
 *   ├──────────────────────────────────────────────┤
 *   │ KPI grid (5 cols at xl, accent border-l-4)    │
 *   │ DataTable (sectionheader gradient, sticky)    │
 *   │ Charts grid (sectionheader + 3-col grid)      │
 *   └──────────────────────────────────────────────┘
 *
 * Section dispatch:
 *   - section with only ``kpi`` items  → KPI grid
 *   - section with a single ``table``  → DataTable
 *   - section with only ``chart`` items → SectionHeader + charts grid
 *   - mixed sections                    → grid honoring item.width
 */
export function PreviewModal({
  open,
  onClose,
  draft,
}: PreviewModalProps): JSX.Element | null {
  const { t, locale } = useT();
  // Keyed per CUSTOMER as well as per draft (Plan E follow-up): draft
  // keys are only unique within one customer, so under the shared fleet
  // origin hp's "gating" preview filters would leak into demo's draft
  // of the same name.
  const storageKey = `preview.filters.${slugFromPath() ?? "-"}.${draft.key}`;
  // Lazy-init from localStorage so the seeding effect in PreviewFilterBar
  // wins the mount race. If we called setActive({}) in a restore effect
  // instead, it would run *after* the child's seed effect and overwrite it.
  const [active, setActive] = useState<Record<string, string>>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, string>;
      }
    } catch {
      /* fall through */
    }
    return {};
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useMemo(
    () => `preview-title-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );

  // Focus trap + auto-focus + restore on close — shared hook.
  useFocusTrap(dialogRef, open);

  // Lock body scroll + ESC to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // If the user navigates between drafts (storageKey changes) while the
  // modal is open, refresh active from the new key's storage. The lazy
  // init above handles the first mount; this effect only handles the
  // cross-draft switch, skipping the initial render to avoid racing
  // with child-effect defaults.
  const firstRestoreRef = useRef(true);
  useEffect(() => {
    if (!open) return;
    if (firstRestoreRef.current) {
      firstRestoreRef.current = false;
      return;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        setActive({});
        return;
      }
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        setActive(parsed as Record<string, string>);
      } else {
        setActive({});
      }
    } catch {
      setActive({});
    }
  }, [open, storageKey]);

  // Persist every change so the next open restores the same selection.
  useEffect(() => {
    if (!open) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(active));
    } catch {
      // Silent — localStorage may be disabled in embedded contexts.
    }
  }, [open, storageKey, active]);

  // A report-group member previews with its GROUP's filters (render only).
  const previewDashboard = useMemo(
    () => withGroupFilters(draft.dashboard, draft.draft),
    [draft.dashboard, draft.draft],
  );
  const flat = useMemo(
    () => flattenForEngine(previewDashboard),
    [previewDashboard],
  );

  // Cross-filter (table cell click): toggle semantics against the
  // modal's local filter state — clicking the already-active value
  // clears the filter; any other value replaces it. Mirrors the
  // portal's ``handleCrossFilter`` in ``GenericDashboardPage``.
  const handleCrossFilter = useCallback(
    (key: string, value: string) => {
      const filt = previewDashboard.global_filters.find((f) => f.key === key);
      setActive((prev) => {
        const next = { ...prev };
        // Shared toggle semantics (`crossFilterWrite`): a multi_select
        // target takes ``["<value>"]`` and clears on the second click.
        const written = crossFilterWrite(filt?.type, prev[key], value);
        if (written === "") delete next[key];
        else next[key] = written;
        return next;
      });
    },
    [previewDashboard.global_filters],
  );

  // KPI action jump (DSA 1.5): the SAME write the portal makes
  // (``applyFilterValues``): an empty value clears, dependents of what
  // changed are invalidated, and the keys the action names are kept.
  // The hand-rolled merge that lived here skipped the cascade, so the
  // Preview kept a stale child the portal would have cleared.
  const handleApplyFilters = useCallback(
    (writes: Record<string, string>) => {
      setActive((prev) => applyFilterValues(prev, writes, flat.filters));
    },
    [flat.filters],
  );

  // SWR (PR-B) — mirror of the portal's global loading hairline,
  // SCOPED to the modal (plan §5 #4): `DashboardSections` reports its
  // fetch-in-flight state up and the bar renders pinned to the top of
  // the dialog.
  const [previewFetching, setPreviewFetching] = useState(false);

  if (!open) return null;

  const allFilters = flat.filters;
  const displayName =
    resolveLocalized(draft.report.display_name as DisplayLabel, locale) ||
    draft.key;
  const description = resolveLocalized(
    draft.report.description as DisplayLabel,
    locale,
  );

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-neutral-100 outline-none"
    >
      {/* Level-1 loading hairline — scoped to the modal (not the app
          chrome behind it). */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[60]">
        <GlobalProgressBar active={previewFetching} fixed={false} />
      </div>
      {/* Top bar — preview chrome */}
      <div className="flex items-center justify-between border-b border-neutral-200 bg-white px-4 py-2.5 sm:px-6">
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <Sparkles size={14} className="text-primary" />
          {t("preview.title")}
        </div>
        <Button
          ghost
          size="sm"
          onClick={onClose}
          leftIcon={<X size={14} />}
        >
          {t("preview.close")}
        </Button>
      </div>

      {/* Body — pixel-identical to hp-portal's GenericDashboardPage */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full px-6 lg:px-10 space-y-3 pb-10 pt-4">
          {/* PAGE HEAD + controls panel — the same ReportHeader and
              ControlPanel the portal page renders (nuevo look, iteración 2). */}
          <div className="-mx-6 -mt-4 border-b border-neutral-200 bg-white px-6 py-3 lg:-mx-10 lg:px-10">
            {/* Encabezado compacto H1c: no date, as in the portal. */}
            <ReportHeader titleId={titleId} title={displayName} description={description} />
          </div>

          {/* Filter surfaces with cascading options, on the light panel */}
          {allFilters.length > 0 && (
            <ControlPanel>
              <PreviewFilterBar
                draft={draft}
                dashboard={previewDashboard}
                filters={allFilters}
                active={active}
                onChange={setActive}
              />
            </ControlPanel>
          )}

          {/* SECTIONS — stacked with the shared SECTION_STACK_CLASS gap
              (PR-3 §1), the same wrapper the portal page puts around its
              DashboardBody. Without it the sections were direct children
              of the page-level space-y-6 (24px) while the portal used
              12px — the mirror wasn't pixel-identical. */}
          {draft.dashboard.sections.length === 0 ? (
            <div className="rounded-xl border border-neutral-200 bg-white p-12 text-center text-sm text-neutral-500 shadow-sm">
              {t("preview.empty")}
            </div>
          ) : (
            <div className={SECTION_STACK_CLASS}>
              <DashboardSections
                sections={draft.dashboard.sections}
                report={draft.report}
                dashboard={previewDashboard}
                activeFilters={active}
                projectSlug={draft.draft?.project_slug}
                onCrossFilter={handleCrossFilter}
                onApplyFilters={handleApplyFilters}
                onFetchActivityChange={setPreviewFetching}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Filter bar wrapper that loads cascading options ──────

export interface PreviewFilterBarProps {
  draft: UseBuilderDraft;
  /** What the preview renders when it is not ``draft.dashboard``: a
   *  report-group member's, with its group's filters merged in. */
  dashboard?: DashboardConfigV2;
  filters: ReadonlyArray<DashboardFilter>;
  active: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

export function PreviewFilterBar({
  draft,
  dashboard: previewDashboard,
  filters,
  active,
  onChange,
}: PreviewFilterBarProps): JSX.Element {
  const { t } = useT();
  const [options, setOptions] = useState<Record<string, string[]>>({});
  const [filtersError, setFiltersError] = useState<string | null>(null);
  // Cajón parity: when the report is configured ``filters_layout: cajon``
  // the Preview mounts the SAME chips row + expandable panel the portal
  // does (shared component), so configuring the choice shows it in place.
  // Same options/active/onChange state the banner bar uses — no fork.
  const dashboard = previewDashboard ?? draft.dashboard;
  const useCajon = dashboard.filters_layout === "cajon";
  // Labelled rows (DSA 1.6): the same shared bar, in its rows layout.
  const useRows = dashboard.filters_layout === "rows";
  const filtersKey = JSON.stringify(active);
  const baseRequest = useMemo<Omit<PreviewRequest, "active_filters">>(
    () => ({
      report_config: draft.report,
      dashboard_config: dashboard,
      // Thread the project slug into /preview/filters so the engine opens
      // the per-project DuckDB — exactly like the KPI/chart/table calls in
      // DashboardSections below. Without it the backend falls back to the
      // legacy shared analytics.duckdb, where a view-kind report's date
      // column isn't present, so fetch_filter_options returns no [min,max]
      // bounds for date-window filters and the DateRangePicker popover
      // renders WITHOUT its in-popover slider row. The portal always
      // targets per-project (slug in the URL) — which is why the slider
      // appeared there but not in the builder Preview.
      project_slug: draft.draft?.project_slug ?? null,
    }),
    [draft.report, dashboard, draft.draft?.project_slug],
  );
  // Track which filter keys we've already applied defaults for so the
  // user can *clear* them — without this, deleting a value triggers the
  // effect again, which re-seeds the default and effectively makes
  // "(ninguno)" impossible to pick.
  const seededKeysRef = useRef<Set<string>>(new Set());
  // Reset the seeded set when the draft switches (different dashboard
  // key → different filters).
  useEffect(() => {
    seededKeysRef.current = new Set();
  }, [draft.key]);

  useEffect(() => {
    if (filters.length === 0) return;

    // Date-window filters (date_range / date_relative / date_slider)
    // don't participate in the cascade options fetch (backend skips
    // them — a date_slider's options entry is its [min, max] domain)
    // and don't get matched against string options.
    // Seed their default_value into the active map directly on the FIRST
    // time we see the filter — after that the user owns the value and
    // clearing it stays cleared.
    // A key is considered "already decided" if it's present in active
    // (even as ""). This distinguishes "user never touched it" (key
    // missing → seed default) from "user cleared it" (key present as ""
    // → respect their choice, even after reload). The in-memory ref
    // handles the mid-session case; the presence-in-active check
    // handles the reload case via localStorage round-trip.
    const dateRangeSeeds: Record<string, string> = {};
    for (const filt of filters) {
      // date_calendar (single day) has no options either and is not a
      // window: same first-load seeding, or a required day with a
      // default never becomes ready (DSA Disponibilidad).
      // A numeric ``range`` threshold default is seeded the same way:
      // no options to match, and the engine applies it anyway.
      if (
        !isDateWindowType(filt.type) &&
        filt.type !== "date_calendar" &&
        filt.type !== "range"
      ) continue;
      if (seededKeysRef.current.has(filt.key)) continue;
      seededKeysRef.current.add(filt.key);
      if (filt.key in active) continue; // user has a value or cleared it
      if (filt.default_value) {
        dateRangeSeeds[filt.key] = filt.default_value;
      }
    }
    if (Object.keys(dateRangeSeeds).length > 0) {
      onChange({ ...active, ...dateRangeSeeds });
      return;
    }

    // Per-generation abort — same pattern as DashboardSections below: a
    // filter change aborts the previous cascade fetch, and previewApi's
    // in-flight dedup absorbs StrictMode's dev double-invoke.
    const controller = new AbortController();
    previewApi
      .filters(
        { ...baseRequest, active_filters: active },
        { signal: controller.signal },
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        setFiltersError(null);
        setOptions(res);

        // Auto-pick a value for every required filter that is still
        // empty, using the same three-tier fallback as hp-portal's
        // GenericDashboardPage:
        //   1. exact match against default_value
        //   2. prefix match (first 2 words uppercase)
        //   3. options[0] as final fallback
        //
        // Date-window filters are skipped — they have no discrete options
        // and their default_value is a JSON preset, already seeded above.
        // We also mark each required filter as "seeded" the first time we
        // pick for it so the user can clear it without re-seeding.
        let updated = reconcileMultiSelectOptions(active, filters, res);
        let changed = updated !== active;
        if (changed) updated = { ...updated };
        for (const filt of filters) {
          if (isDateWindowType(filt.type)) continue;
          if (!filt.required || updated[filt.key]) continue;
          if (filt.depends_on == null && seededKeysRef.current.has(filt.key)) continue;
          if (filt.depends_on?.some((key) =>
            filters.some((parent) => parent.key === key && parent.required && !active[key])
          )) continue;
          const opts = res[filt.key] ?? [];
          const seed = resolveSeedValue(filt, opts);
          if (seed === null) continue;
          seededKeysRef.current.add(filt.key);
          if (!changed) updated = { ...updated };
          updated[filt.key] = seed;
          changed = true;
        }
        if (changed) onChange(updated);
      })
      .catch((err: unknown) => {
        // A superseded generation's AbortError lands here too — that one is
        // not a failure. Anything else is: show it instead of empty dropdowns
        // and numbers computed without the required filters (the portal does
        // the same: GenericDashboardPage "Review #56 #5").
        if (controller.signal.aborted) return;
        setFiltersError(err instanceof Error ? err.message : String(err));
      });
    return () => controller.abort();
    // baseRequest is stable across the same filtersKey snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Range filter histograms: the same engine call as the portal, over
  // the draft. New identity per filter change → an open panel refetches.
  const fetchRangeHistogram = useCallback(
    (filterKey: string, edges: number[], signal: AbortSignal) =>
      previewApi.rangeHistogram(
        { ...baseRequest, active_filters: active, filter_key: filterKey, edges },
        { signal },
      ),
    [baseRequest, active],
  );

  const errorLine =
    filtersError !== null ? (
      <p
        role="alert"
        className="mx-4 mb-3 mt-1 rounded-md border border-error/40 bg-white px-3 py-1.5 text-xs text-error-dark"
      >
        ⚠ {t("preview.filtersError", { err: filtersError })}
      </p>
    ) : null;

  if (useCajon) {
    // Same wiring as hp-portal's GenericDashboardPage host — the shared
    // component owns the expand/collapse; the panel lives in the flow so
    // it works identically inside the preview modal (no anchoring needed).
    return (
      <div>
        <CollapsibleFilterHeader
          surface="light"
          filters={filters as never}
          filterOptions={options}
          activeFilters={active}
          onFilterChange={(key, value) => {
            const filt = filters.find((f) => f.key === key);
            if (!filt) return;
            onChange(setFilterValue(active, filt, value, filters));
          }}
          onClear={() =>
            onChange(resetAllFilters(filters, options, new Date()))
          }
          onResetFilter={(filt) =>
            onChange(
              resetFilterValue(active, filt, options[filt.key] ?? [], new Date(), filters),
            )
          }
          fetchRangeHistogram={fetchRangeHistogram}
          today={new Date()}
        />
        {errorLine}
      </div>
    );
  }

  return (
    <>
      <GenericFilterBar
        embedded
        surface="light"
        layout={useRows ? "rows" : "wrap"}
        filters={filters}
        filterOptions={options}
        activeFilters={active}
        onFilterChange={(key, value) => {
          const filt = filters.find((f) => f.key === key);
          if (!filt) return;
          // Shared write semantics — same as the portal (date-window clear
          // sentinel vs drop-key). No builder fork.
          onChange(setFilterValue(active, filt, value, filters));
        }}
        // "Clear all" resets to the report defaults (shared semantics),
        // not a blank ``{}`` — matches the portal so the Preview shows the
        // same thing the end user sees.
        onClear={() => onChange(resetAllFilters(filters, options, new Date()))}
        fetchRangeHistogram={fetchRangeHistogram}
      />
      {errorLine}
    </>
  );
}

// ── Sections renderer — KPIs / Table / Charts dispatch ──

/**
 * Item selection callback fired when the user clicks a rendered KPI /
 * chart / table / text inside a section. Set when the renderer is
 * embedded inside the Dashboard tab (where clicks open the
 * read-only inspector) — undefined in the PreviewModal case where
 * the dashboard is a passive read.
 */
export type DashboardSelectItem = (sectionId: string, itemId: string) => void;
export type DashboardSelectSection = (sectionId: string) => void;

export interface DashboardSectionsProps {
  sections: ReadonlyArray<SectionConfig>;
  report: ReportConfig;
  dashboard: DashboardConfigV2;
  activeFilters: Record<string, string>;
  /** Iter 30 — project slug, threaded into every preview request so
   *  the engine opens the per-project DuckDB file instead of the
   *  legacy shared ``analytics.duckdb``. Without it, table previews
   *  on view-kind reports 500 ("table ... does not exist"). */
  projectSlug?: string | null;
  /** Iter 21 — when set, clicking an item fires this callback (used by
   *  the Dashboard tab's inspector). Undefined in PreviewModal. */
  onSelectItem?: DashboardSelectItem;
  /** Iter 21 — when set, clicking a section header fires this. */
  onSelectSection?: DashboardSelectSection;
  /** Iter 21 — the currently-selected item id, used to draw the
   *  selection ring. */
  selectedItemId?: string | null;
  /** Cross-filter: host callback that toggles a report filter when the
   *  user clicks a cell in a ``filter_key`` table column. Undefined
   *  (e.g. the Dashboard-tab canvas) = cells render as plain text. */
  onCrossFilter?: (filterKey: string, rawValue: string) => void;
  /** KPI action jump: the host writes these filters. */
  onApplyFilters?: (filters: Record<string, string>) => void;
  /** SWR (PR-B) — reports whether ANY preview fetch is in flight
   *  (kpis/charts + table/drill via the wrapped data source), so the
   *  host can drive a scoped ``GlobalProgressBar``. Undefined (e.g.
   *  the Dashboard-tab canvas) = no reporting. */
  onFetchActivityChange?: (active: boolean) => void;
}

/**
 * Builder adapter for the shared ``DashboardBody``'s injected fetch contract
 * (P2.3). Wraps ``previewApi`` (→ ``/api/builder/preview/*``), closing over the
 * draft payload (``report_config`` + ``dashboard_config`` + ``project_slug``)
 * so the shared body never knows about those builder-only params. The legacy
 * singular table's ``MAIN_TABLE_ID`` sentinel maps to the engine's ``"main"``.
 */
function makeBuilderDataSource(
  base: Pick<PreviewRequest, "report_config" | "dashboard_config" | "project_slug">,
): DashboardDataSource {
  return {
    // The builder preview/table endpoint has no server-side ``search`` param,
    // so the shared body's search box is a no-op here (minor parity gap).
    fetchTableData: ({ tableId, filters, sortBy, sortDir, limit, offset }: FetchTableDataArgs) =>
      previewApi.table({
        ...base,
        active_filters: filters,
        table_id: tableId === MAIN_TABLE_ID ? "main" : tableId,
        sort_by: sortBy ?? null,
        sort_dir: sortDir,
        limit,
        offset,
      }) as unknown as Promise<TableData>,
    fetchDrill: ({ drillId, filters }: FetchDrillArgs) =>
      previewApi.drill({
        ...base,
        drill_id: drillId,
        active_filters: filters,
      }) as unknown as Promise<DrillPayload>,
    fetchRowDrill: ({ tableId, drillId, rowValue, filters }: FetchRowDrillArgs) =>
      previewApi.tableRowDrill({
        ...base,
        table_id: tableId,
        drill_id: drillId,
        row_value: rowValue,
        active_filters: filters,
      }) as unknown as Promise<RowDrillData>,
  };
}

export function DashboardSections({
  report,
  dashboard,
  activeFilters,
  projectSlug,
  onSelectItem,
  onSelectSection,
  selectedItemId,
  onCrossFilter,
  onApplyFilters,
  onFetchActivityChange,
}: DashboardSectionsProps): JSX.Element {
  const { t } = useT();
  const baseRequest: Omit<PreviewRequest, "active_filters"> = {
    report_config: report,
    dashboard_config: dashboard,
    // Iter 30 — target the per-project DuckDB file. Omitting this made
    // the backend fall back to the legacy shared analytics.duckdb,
    // which lacks per-project view-kind tables (gating_fact_seller_*),
    // so multi-table report previews 500'd.
    project_slug: projectSlug ?? null,
  };

  // P2.3 — the dashboard body is now the SHARED ``DashboardBody`` (same
  // component the portal renders), fed the flattened engine-shape config + a
  // previewApi data-source adapter. The builder-only ``project_slug`` lives
  // inside the adapter; the shared body never sees it.
  const renderConfig = useMemo(() => flattenForRender(dashboard), [dashboard]);

  // SWR (PR-B) — same fetch-activity counter the portal page uses;
  // table/drill fetches from the shared body are counted through the
  // wrapped data source, and the host is notified so it can render the
  // scoped hairline.
  const { active: fetchActive, track, wrapDataSource } = useFetchActivity();
  useEffect(() => {
    onFetchActivityChange?.(fetchActive);
  }, [fetchActive, onFetchActivityChange]);
  useEffect(
    () => () => onFetchActivityChange?.(false),
    [onFetchActivityChange],
  );

  const builderDataSource = useMemo(
    () =>
      wrapDataSource(
        makeBuilderDataSource({
          report_config: report,
          dashboard_config: dashboard,
          project_slug: projectSlug ?? null,
        }),
      ),
    [report, dashboard, projectSlug, wrapDataSource],
  );

  const [kpis, setKpis] = useState<Array<Record<string, unknown>>>([]);
  const [charts, setCharts] = useState<Array<Record<string, unknown>>>([]);
  // Dynamic text blocks (QA F08): the portal fetches them as a third
  // block; the preview did not, so every text block rendered empty.
  const [texts, setTexts] = useState<Array<Record<string, unknown>>>([]);
  // SWR (PR-B, portal mirror): per-block loading + first-settle marker.
  // Before the first settle → the historical "Loading preview…" spinner
  // (nothing to keep on screen). After it, a filter/config change is a
  // REFRESH: the body stays mounted, blocks dim independently and
  // transition when THEIR data lands — no more full-body swap.
  const [kpisLoading, setKpisLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [textsLoading, setTextsLoading] = useState(true);
  const [hasSettledOnce, setHasSettledOnce] = useState(false);
  // Per-block errors (review #56 #2 mirror): a failed block surfaces
  // in-slot (no data) or as a stale marker (data on screen) inside the
  // shared body, with a per-block retry.
  const [blockErrors, setBlockErrors] = useState<{
    kpis: string | null;
    charts: string | null;
    texts: string | null;
  }>({ kpis: null, charts: null, texts: null });
  const error = blockErrors.kpis ?? blockErrors.charts ?? blockErrors.texts;

  const reqKey = JSON.stringify({ r: report, d: dashboard, f: activeFilters });

  // Latest request payload, readable from the stable `runBlock` without
  // widening its deps (render-time ref mirror — the codebase's standard
  // pattern for stable callbacks over fresh values).
  const requestRef = useRef({ baseRequest, activeFilters });
  requestRef.current = { baseRequest, activeFilters };
  const controllerRef = useRef<AbortController | null>(null);

  /** Launch ONE preview family (kpis | charts | texts) under `signal`.
   *  Shared by the load effect and the per-block retries (review #56 #2). */
  const runBlock = useCallback(
    (
      block: "kpis" | "charts" | "texts",
      signal: AbortSignal,
      onSettled?: (succeeded: boolean) => void,
    ): void => {
      const { baseRequest: base, activeFilters: filters } = requestRef.current;
      const setLoading =
        block === "kpis" ? setKpisLoading : block === "charts" ? setChartsLoading : setTextsLoading;
      setLoading(true);
      const body = { ...base, active_filters: filters };
      const promise =
        block === "kpis"
          ? previewApi.kpis(body, { signal })
          : block === "charts"
            ? previewApi.charts(body, { signal })
            : previewApi.texts(body, { signal });
      let succeeded = false;
      track(promise)
        .then((payload) => {
          if (signal.aborted) return;
          succeeded = true;
          if (block === "kpis") setKpis(payload);
          else if (block === "charts") setCharts(payload);
          else setTexts(payload);
          setBlockErrors((prev) =>
            prev[block] === null ? prev : { ...prev, [block]: null },
          );
        })
        .catch((err: unknown) => {
          // Aborted → this generation was superseded; the new one owns state.
          if (signal.aborted) return;
          setBlockErrors((prev) => ({
            ...prev,
            [block]: err instanceof Error ? err.message : "Preview failed",
          }));
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
          onSettled?.(succeeded);
        });
    },
    [track],
  );

  useEffect(() => {
    // Per-generation abort: changing inputs (or unmounting — including
    // StrictMode's dev remount) aborts the previous kpis+charts pair, so a
    // superseded response can neither land a stale payload nor a stale
    // error (the A→B→A toggle race). The in-flight dedup inside
    // ``previewApi`` collapses StrictMode's synchronous abort+refetch back
    // to a single network call per family — no last-key/mounted refs here.
    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;
    setBlockErrors({ kpis: null, charts: null, texts: null });
    // Both blocks settled with at least one SUCCESS → the body has
    // something to mount and takes over (per-block error surfacing
    // handles the failed sibling). Both FAILED on the first round →
    // `hasSettledOnce` stays false and the compact error view below
    // renders instead of an empty body.
    let remaining = 3;
    let anySucceeded = false;
    const settled = (succeeded: boolean): void => {
      anySucceeded = anySucceeded || succeeded;
      remaining -= 1;
      if (remaining === 0 && !signal.aborted && anySucceeded) {
        setHasSettledOnce(true);
      }
    };
    runBlock("kpis", signal, settled);
    runBlock("charts", signal, settled);
    runBlock("texts", signal, settled);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqKey]);

  // Per-block retries — relaunch ONLY the failed family inside the
  // current generation.
  const retryBlock = useCallback(
    (block: "kpis" | "charts" | "texts"): void => {
      let controller = controllerRef.current;
      if (controller == null || controller.signal.aborted) {
        controller = new AbortController();
        controllerRef.current = controller;
      }
      const { signal } = controller;
      runBlock(block, signal, (succeeded) => {
        if (succeeded && !signal.aborted) setHasSettledOnce(true);
      });
    },
    [runBlock],
  );
  const retryKpis = useCallback(() => retryBlock("kpis"), [retryBlock]);
  const retryCharts = useCallback(() => retryBlock("charts"), [retryBlock]);
  const retryTexts = useCallback(() => retryBlock("texts"), [retryBlock]);

  // Early returns must come AFTER every hook call so the hook counter
  // stays stable across renders (React #310 guard).
  //
  // Review #56 #3: the error early-return only applies BEFORE the first
  // settle (nothing on screen worth keeping). Once the body has data, a
  // transient refresh error must NOT unmount it — the body stays and a
  // compact error banner renders above the canvas (plus the per-block
  // in-body surfacing).
  if (error && !hasSettledOnce) {
    return (
      <div className="rounded-xl border border-error/40 bg-error/5 px-4 py-3 text-sm text-error-dark shadow-sm">
        {error}
      </div>
    );
  }

  if (!hasSettledOnce) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
        <Spinner size={16} ariaLabel={t("common.loading")} />
        {t("preview.loadingCanvas")}
      </div>
    );
  }

  return (
    <>
      {error && (
        <div
          role="alert"
          data-preview-error-banner
          className="mb-2 rounded-xl border border-error/40 bg-error/5 px-4 py-2 text-xs text-error-dark shadow-sm"
        >
          {error}
        </div>
      )}
      <DashboardBody
        config={renderConfig}
        kpis={kpis as unknown as Kpi[]}
        charts={charts as unknown as ChartData[]}
        texts={texts as unknown as TextValue[]}
        activeFilters={activeFilters}
        data={builderDataSource}
        instanceKey={projectSlug ?? "preview"}
        onCrossFilter={onCrossFilter}
        onApplyFilters={onApplyFilters}
        refreshState={{
          kpis: {
            refreshing: kpisLoading,
            error: blockErrors.kpis,
            onRetry: retryKpis,
          },
          charts: {
            refreshing: chartsLoading,
            error: blockErrors.charts,
            onRetry: retryCharts,
          },
          texts: {
            refreshing: textsLoading,
            error: blockErrors.texts,
            onRetry: retryTexts,
          },
        }}
        onSelectItem={onSelectItem}
        onSelectSection={onSelectSection}
        selectedItemId={selectedItemId}
      />
    </>
  );
}
